import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import {
  VoicesPerson,
  VoicesPublishingSettings,
} from "../../../Models/voicesProfiles";
import { useUpdateVoicesPerson } from "../../../Query/useVoicesProfiles";
import { ComoButton } from "../../Common/ComoButton";
import VoicesField from "../Common/VoicesField";
import VoicesPublishingFields from "./VoicesPublishingFields";

interface Props {
  person: VoicesPerson | null;
  onClose: () => void;
}

const EMPTY_SETTINGS: VoicesPublishingSettings = {
  buffer_channel_id: null,
  weekly_limit: null,
  blocked_terms: [],
};

const VoicesPersonDialog: React.FC<Props> = ({ person, onClose }) => {
  const { t } = useTranslation("voicesProfiles");
  const updatePerson = useUpdateVoicesPerson();
  const [settings, setSettings] =
    useState<VoicesPublishingSettings>(EMPTY_SETTINGS);

  useEffect(() => {
    const profile = person?.profile;
    setSettings(
      profile
        ? {
            buffer_channel_id: profile.buffer_channel_id,
            weekly_limit: profile.weekly_limit,
            blocked_terms: profile.blocked_terms,
          }
        : EMPTY_SETTINGS,
    );
  }, [person]);

  const handleSave = () => {
    if (!person) return;
    updatePerson.mutate(
      { personId: person.user_id, data: settings },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open={!!person} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: color.gray900, fontWeight: 600 }}>
        {person && `${person.name} ${person.surname}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <VoicesPublishingFields
            value={settings}
            channelType="profile"
            onChange={setSettings}
          />
          <VoicesField label={t("preferences")} hint={t("preferencesReadOnlyHint")}>
            <Typography
              variant="body2"
              sx={{ color: color.gray700, whiteSpace: "pre-wrap" }}
            >
              {person?.profile?.preferences || t("noPreferences")}
            </Typography>
          </VoicesField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: color.gray600, textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <ComoButton onClick={handleSave} disabled={updatePerson.isPending}>
          {t("save")}
        </ComoButton>
      </DialogActions>
    </Dialog>
  );
};

export default VoicesPersonDialog;
